import { useState, useCallback, useRef } from 'react';
import { api, LitLegionAdminTaskStatusWithTask, LitLegionTaskStatusResponse } from '@/lib/api';

interface UseAdminUserTaskStatusesReturn {
  statuses: LitLegionAdminTaskStatusWithTask[];
  total: number;
  loading: boolean;
  error: string | null;
  loadedUsername: string | null;
  resolvedUserId: string | null;
  fetchForUsername: (username: string) => void;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
  applyLocalUpdate: (taskStatus: LitLegionTaskStatusResponse) => void;
  applyLocalDelete: (taskStatusId: string) => void;
  /** Empty the list after a bulk delete, without a round-trip. */
  applyLocalClear: () => void;
  /**
   * Splice a freshly-created task-status row into the list. Used by both the
   * "Add status" form and the delete-then-recreate flow when the server
   * automatically re-makes the row as 'available'. Triggers a refresh so the
   * task panel (with the joined ll_tasks row) is fully populated.
   */
  applyLocalAdd: (taskStatus: LitLegionTaskStatusResponse) => void;
}

const PAGE_SIZE = 50;

export function useAdminUserTaskStatuses(): UseAdminUserTaskStatusesReturn {
  const [statuses, setStatuses] = useState<LitLegionAdminTaskStatusWithTask[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [loadedUsername, setLoadedUsername] = useState<string | null>(null);
  const [resolvedUserId, setResolvedUserId] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchPage = useCallback(async (username: string, newOffset: number, append: boolean) => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      setError(null);
      const result = await api.adminListLitLegionUserTaskStatuses(username, {
        limit: PAGE_SIZE,
        offset: newOffset,
      });
      if (fetchIdRef.current !== id) return;
      setStatuses((prev) => (append ? [...prev, ...result.statuses] : result.statuses));
      setTotal(result.total);
      setOffset(newOffset);
      setLoadedUsername(result.username);
      setResolvedUserId(result.userId);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load task statuses');
      if (!append) {
        setStatuses([]);
        setTotal(0);
        setLoadedUsername(null);
        setResolvedUserId(null);
      }
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  const fetchForUsername = useCallback(
    (username: string) => {
      fetchPage(username, 0, false);
    },
    [fetchPage],
  );

  const refresh = useCallback(() => {
    if (loadedUsername) {
      fetchPage(loadedUsername, 0, false);
    }
  }, [fetchPage, loadedUsername]);

  const loadMore = useCallback(() => {
    if (!loading && loadedUsername && offset + PAGE_SIZE < total) {
      fetchPage(loadedUsername, offset + PAGE_SIZE, true);
    }
  }, [fetchPage, loadedUsername, loading, offset, total]);

  const applyLocalUpdate = useCallback((taskStatus: LitLegionTaskStatusResponse) => {
    setStatuses((prev) =>
      prev.map((s) => (s.taskStatusId === taskStatus.taskStatusId ? { ...s, ...taskStatus } : s)),
    );
  }, []);

  const applyLocalDelete = useCallback((taskStatusId: string) => {
    setStatuses((prev) => prev.filter((s) => s.taskStatusId !== taskStatusId));
    setTotal((prev) => Math.max(0, prev - 1));
  }, []);

  const applyLocalClear = useCallback(() => {
    setStatuses([]);
    setTotal(0);
    setOffset(0);
  }, []);

  // Refetch on add. Re-using fetchPage gives us the joined task row, the
  // accurate total, and consistent ordering — splicing locally would skip
  // the server's `LEFT JOIN ll_tasks` and leave `task: null` until refresh.
  const applyLocalAdd = useCallback(
    (_taskStatus: LitLegionTaskStatusResponse) => {
      if (loadedUsername) {
        fetchPage(loadedUsername, 0, false);
      }
    },
    [fetchPage, loadedUsername],
  );

  return {
    statuses,
    total,
    loading,
    error,
    loadedUsername,
    resolvedUserId,
    fetchForUsername,
    refresh,
    loadMore,
    hasMore: offset + PAGE_SIZE < total,
    applyLocalUpdate,
    applyLocalDelete,
    applyLocalClear,
    applyLocalAdd,
  };
}
